// User identity generation and management
// Uses localStorage with room-specific keys to maintain identity across tabs

import { ref, watchEffect } from 'vue'
import { useAuth } from '../composables/useAuth'
import { useSubscription } from '../composables/useSubscription'

const adjectives = [
  'OffKey', 'Pitchy', 'Screechy', 'Warbly', 'Squeaky', 'Raspy', 'Breathy', 'Shaky',
  'Belting', 'Dramatic', 'Tipsy', 'Brave', 'Bold', 'Fearless', 'Shameless', 'Confident',
  'Shower', 'Karaoke', 'Wannabe', 'Aspiring', 'Hopeful', 'Eager', 'Passionate', 'Wild',
  'Mic', 'Stage', 'Spotlight', 'Encore', 'Viral', 'Epic', 'Legendary', 'Iconic'
];

const nouns = [
  'Rockstar', 'Diva', 'Legend', 'Icon', 'Star', 'Idol', 'Hero', 'King',
  'Crooner', 'Belter', 'Screamer', 'Howler', 'Yodeler', 'Mumbler', 'Hummer', 'Whistler',
  'Superstar', 'Sensation', 'Maestro', 'Vocalist', 'Singer', 'Artist', 'Performer', 'Talent',
  'Slayer', 'Crusher', 'Destroyer', 'Killer', 'Master', 'Champion', 'Warrior', 'Beast'
];

const pastelColors = [
  '#FFB3BA', // pastel red
  '#FFDFBA', // pastel orange
  '#FFFFBA', // pastel yellow
  '#BAFFC9', // pastel green
  '#BAE1FF', // pastel blue
  '#E0BBE4', // pastel purple
  '#FFDFD3', // pastel peach
  '#D4F1F4', // pastel cyan
  '#FFD6E8', // pastel pink
  '#C7CEEA', // pastel lavender
  '#B5EAD7', // pastel mint
  '#FFC8A2', // pastel coral
  '#A8DADC', // pastel teal
  '#F1C0E8', // pastel magenta
  '#CFBAF0', // pastel violet
  '#90DBF4', // pastel sky blue
];

/**
 * Generates a random user identity with name and color
 * @returns {Object} { userName, userColor }
 */
export function generateUserIdentity() {
  const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = nouns[Math.floor(Math.random() * nouns.length)];
  const color = pastelColors[Math.floor(Math.random() * pastelColors.length)];
  
  return {
    userName: `${adjective}${noun}`,
    userColor: color
  };
}

/**
 * Gets or creates user identity for a specific room
 * Identity is stored in localStorage with room-specific key
 * @param {string} roomId - The room ID
 * @returns {Object} { userName, userColor }
 */
export function getUserIdentity(roomId) {
  const storageKey = `karaoke_user_${roomId}`;
  
  // Try to get existing identity for this room
  const existingIdentity = localStorage.getItem(storageKey);
  
  if (existingIdentity) {
    try {
      return JSON.parse(existingIdentity);
    } catch (error) {
      console.error('Error parsing stored user identity:', error);
      // Fall through to generate new identity
    }
  }
  
  // Generate new identity for this room
  const newIdentity = generateUserIdentity();
  
  // Store it
  try {
    localStorage.setItem(storageKey, JSON.stringify(newIdentity));
  } catch (error) {
    console.error('Error storing user identity:', error);
  }
  
  return newIdentity;
}

/**
 * Clears user identity for a specific room
 * @param {string} roomId - The room ID
 */
export function clearUserIdentity(roomId) {
  const storageKey = `karaoke_user_${roomId}`;
  localStorage.removeItem(storageKey);
}

/**
 * Reactive composable that returns user identity for a room.
 * For premium users with a customDisplayName, uses that instead of the random name.
 * @param {string} roomId - The room ID
 * @returns {{ userIdentity: import('vue').Ref<{userName: string, userColor: string, isCustomName: boolean}> }}
 */
export function useUserIdentity(roomId) {
  const baseIdentity = getUserIdentity(roomId)
  const userIdentity = ref({ ...baseIdentity, isCustomName: false })

  const { userProfile } = useAuth()
  const { isPremium } = useSubscription()

  watchEffect(() => {
    const customName = userProfile.value?.customDisplayName
    if (isPremium.value && customName) {
      userIdentity.value = {
        userName: customName,
        userColor: baseIdentity.userColor,
        isCustomName: true
      }
    } else {
      userIdentity.value = {
        ...baseIdentity,
        isCustomName: false
      }
    }
  })

  return { userIdentity }
}

